import { useState } from 'react';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface ToSModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  tosContent: string;
  tosVersion: string;
  tosDate: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const ToSModal = ({ isOpen, onClose, onAccept, tosContent, tosVersion, tosDate, loading = false, error = null, onRetry }: ToSModalProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasReadAcknowledgment, setHasReadAcknowledgment] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    
    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    if (!hasReadAcknowledgment || !hasScrolledToBottom) {
      return;
    }
    onAccept();
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">תקנון ותנאי שימוש</h2>
            <p className="text-sm text-gray-500 mt-1">גרסה {tosVersion} | {tosDate}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="סגור"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 prose prose-sm max-w-none"
          style={{ direction: 'rtl', textAlign: 'right' }}
        >
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              טוען תנאי שימוש...
            </div>
          )}
          {error && (
            <div className="py-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
                >
                  נסה שוב
                </button>
              )}
            </div>
          )}
          {!loading && !error && (
            <ReactMarkdown>{tosContent || 'אין תוכן להצגה.'}</ReactMarkdown>
          )}
        </div>

        {/* Footer with acknowledgment and actions */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          {!hasScrolledToBottom && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                אנא גלול למטה וקרא את כל התקנון לפני שתמשיך
              </p>
            </div>
          )}
          
          <div className="flex items-start mb-4">
            <input
              type="checkbox"
              id="tos-acknowledgment"
              checked={hasReadAcknowledgment}
              onChange={(e) => setHasReadAcknowledgment(e.target.checked)}
              disabled={!hasScrolledToBottom || loading}
              className="mt-1 ml-3 h-5 w-5 text-black focus:ring-black border-gray-300 rounded disabled:opacity-50"
            />
            <label
              htmlFor="tos-acknowledgment"
              className="text-sm text-gray-700 select-none cursor-pointer"
            >
              קראתי והבנתי את תנאי השימוש ואני מסכים/ה לכל התנאים המפורטים לעיל. אני מצהיר/ה כי אני בעל/ת הסמכות לחייב את העסק שלי בהסכם זה.
            </label>
          </div>

          <div className="flex gap-3 justify-start">
            <button
              onClick={handleAccept}
              disabled={!hasScrolledToBottom || !hasReadAcknowledgment || loading}
              className="px-6 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
            >
              {loading ? 'שומר...' : 'אשר והמשך'}
            </button>
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
            >
              ביטול
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToSModal;
